import { initializeApp } from "@firebase/app";
import {
  signOut as firebaseSignOut,
  getAuth,
  isSignInWithEmailLink,
  parseActionCodeURL,
  signInWithEmailLink,
} from "@firebase/auth";

import { env } from "~/lib/env";

const app = initializeApp({
  apiKey: env.firebaseApiKey,
  authDomain: env.firebaseAuthDomain,
});

// Each parent partner has its own Identity Platform tenant. The sign-in link the api
// generates carries the tenant id; it is set on `auth` before signing in and
// persists with the signed-in user, so later sessions need nothing from the env.
export const auth = getAuth(app);

export function authReady(): Promise<void> {
  return auth.authStateReady();
}

const SIGN_IN_EMAIL_KEY = "portal-sign-in-email";

export function rememberSignInEmail(email: string): boolean {
  try {
    localStorage.setItem(SIGN_IN_EMAIL_KEY, email);
    return true;
  } catch {
    return false;
  }
}

export function recallSignInEmail(): string | null {
  try {
    return localStorage.getItem(SIGN_IN_EMAIL_KEY);
  } catch {
    return null;
  }
}

function forgetSignInEmail(): boolean {
  try {
    localStorage.removeItem(SIGN_IN_EMAIL_KEY);
    return true;
  } catch {
    return false;
  }
}

export function isSignInLink(url: string): boolean {
  return isSignInWithEmailLink(auth, url);
}

/** Thrown when a sign-in link names no tenant, so signing in could only hit the wrong pool. */
export class MissingTenantError extends Error {
  readonly code = "portal/missing-tenant";
}

/** The tenant from the link itself, or from the continue URL the api also put it on. */
function tenantIdFromLink(url: string): string | null {
  return parseActionCodeURL(url)?.tenantId ?? new URL(url).searchParams.get("tenantId");
}

export async function completeSignIn(email: string, url: string): Promise<void> {
  const tenantId = tenantIdFromLink(url);
  if (!tenantId) throw new MissingTenantError("Sign-in link has no tenant id");
  auth.tenantId = tenantId;
  await signInWithEmailLink(auth, email, url);
  forgetSignInEmail();
}

export function signOut(): Promise<void> {
  return firebaseSignOut(auth);
}
